// /home/ubuntu/avaai_dashboard/frontend/src/pages/ResetPassword.tsx

import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { usePreferences } from "../context/PreferencesContext";
import {
  getAvaaioAuthCopy,
  localizeAuthResponse,
} from "../utils/avaaioAuthCopy";

const API_BASE = "";

function getErrorText(value: any): string {
  if (!value) return "Reset failed.";
  if (typeof value === "string") return value;
  if (Array.isArray(value)) {
    return value.map(getErrorText).filter(Boolean).join(" ");
  }
  if (typeof value === "object") {
    return (
      value.message ||
      value.msg ||
      value.error ||
      value.detail && getErrorText(value.detail) ||
      JSON.stringify(value)
    );
  }
  return String(value);
}


export default function ResetPassword() {
  const { preferences } = usePreferences();
  const copy = getAvaaioAuthCopy(
    preferences.language
  );

  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleReset() {
    if (loading) return;

    if (password !== confirm) {
      setMsg(copy.reset.passwordMismatch);
      return;
    }

    setLoading(true);
    setMsg("");

    try {
      const res = await fetch(`${API_BASE}/api/v1/auth/reset-password`, { credentials: "include",
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          token,
          password,
          new_password: password,
          ui_language: copy.language,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        const message =
  typeof data?.detail === "string"
    ? data.detail
    : data?.detail?.message
    || data?.message
    || JSON.stringify(data?.detail)
    || "Reset failed";

throw new Error(message);
      }

      setMsg(copy.reset.success);
        setTimeout(() => {
          window.location.href = "/login";
        }, 1500);
    } catch (err: any) {
      setMsg(
        localizeAuthResponse(
          getErrorText(err?.message || err),
          copy.reset.failed,
          copy
        )
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.brand}>AVAAIO™</div>

        <h1 style={styles.title}>{copy.reset.title}</h1>

        <p style={styles.subtitle}>
          {copy.reset.subtitle}
        </p>

        <input
          type="password"
          placeholder={copy.reset.newPassword}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          style={styles.input}
        />

        <input
          type="password"
          placeholder={copy.reset.confirmPassword}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          style={styles.input}
        />

        <button
          onClick={handleReset}
          style={{
            ...styles.button,
            ...(loading ? styles.disabled : {}),
          }}
        >
          {loading ? copy.reset.updating : copy.reset.submit}
        </button>

        <button
          onClick={() => (window.location.href = "/login")}
          style={styles.back}
        >
          {copy.common.backLogin}
        </button>

        {msg && <p style={styles.msg}>{msg}</p>}

        <div style={styles.footer}>
          <div>© 2026 American Globalo, LLC</div>

          <div>
            <Link to="/privacy-policy">{copy.common.privacy}</Link> |{" "}
            <Link to="/terms-and-conditions">{copy.common.terms}</Link> |{" "}
            <Link to="/ai-disclaimer">{copy.common.aiDisclaimer}</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles: any = {
  page: {
    minHeight: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    background:
      "linear-gradient(180deg, #00050d 0%, #000814 50%, #00030a 100%)",
    padding: "20px",
    fontFamily: "Inter, sans-serif",
  },

  card: {
    width: "100%",
    maxWidth: "420px",
    padding: "30px",
    borderRadius: "20px",
    background: "rgba(8,16,28,0.95)",
    border: "1px solid rgba(0,255,255,0.2)",
    textAlign: "center",
    boxShadow: "0 0 40px rgba(0,255,255,0.1)",
  },

  brand: {
    color: "#00ffff",
    fontSize: "12px",
    letterSpacing: "0.3em",
    marginBottom: "10px",
  },

  title: {
    color: "#fff",
    fontSize: "28px",
    marginBottom: "10px",
  },

  subtitle: {
    color: "#94a3b8",
    fontSize: "14px",
    marginBottom: "20px",
  },

  input: {
    width: "100%",
    padding: "14px",
    borderRadius: "12px",
    border: "1px solid #334155",
    background: "#020617",
    color: "#fff",
    marginBottom: "12px",
  },

  button: {
    width: "100%",
    padding: "14px",
    borderRadius: "12px",
    border: "none",
    background: "#00ffff",
    color: "#000",
    fontWeight: "bold",
    cursor: "pointer",
  },

  disabled: {
    opacity: 0.6,
  },

  back: {
    marginTop: "10px",
    background: "none",
    border: "none",
    color: "#00ffff",
    cursor: "pointer",
  },

  msg: {
    marginTop: "12px",
    color: "#22c55e",
    fontSize: "13px",
  },

  footer: {
    marginTop: "18px",
    fontSize: "12px",
    color: "#64748b",
  },
};
